'use client';

import React, { useState, useEffect } from 'react';
import { isInstagramOrFacebookInAppBrowser, getBrowserInfo } from '@/lib/browserDetection';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { ExternalLink, X, Smartphone } from 'lucide-react';

interface InAppBrowserWarningProps {
  show: boolean;
  onClose: () => void;
  error?: Error;
}

const InAppBrowserWarning: React.FC<InAppBrowserWarningProps> = ({ 
  show, 
  onClose, 
  error 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [browserInfo, setBrowserInfo] = useState(getBrowserInfo());

  useEffect(() => {
    // Only show if we're in an in-app browser and there's an error
    const shouldShow = show && isInstagramOrFacebookInAppBrowser();
    setIsVisible(shouldShow);
    
    if (shouldShow) {
      setBrowserInfo(getBrowserInfo());
    }
  }, [show]);

  const handleOpenInBrowser = () => {
    // Get current URL
    const currentUrl = window.location.href;
    
    // Try to open in external browser
    // This works on iOS Safari and some Android browsers
    if (navigator.userAgent.includes('iPhone') || navigator.userAgent.includes('iPad')) {
      // For iOS, try to open in Safari
      window.open(currentUrl, '_blank');
    } else {
      // For Android and others, try to open in default browser
      window.open(currentUrl, '_blank');
    }
    
    onClose();
  };

  const handleCopyLink = () => {
    const currentUrl = window.location.href;
    navigator.clipboard.writeText(currentUrl).then(() => {
      // You could show a toast notification here
      console.log('Link copied to clipboard');
    }).catch(() => {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = currentUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    });
  };

  if (!isVisible) {
    return null;
  }

  return (
    <Dialog open={isVisible} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <Smartphone className="w-5 h-5 text-blue-600" />
            </div>
            <DialogTitle className="text-lg font-semibold">
              For the best experience
            </DialogTitle>
          </div>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="text-sm text-gray-600">
            <p className="mb-2">
              You're currently using an in-app browser which may cause compatibility issues.
            </p>
            <p>
              For the best experience, please open this link in your device's default browser 
              (Safari, Chrome, etc.).
            </p>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">
                <strong>Error detected:</strong> {error.message}
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Button 
              onClick={handleOpenInBrowser}
              className="w-full"
              size="sm"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Open in Browser
            </Button>
            
            <Button 
              onClick={handleCopyLink}
              variant="outline"
              className="w-full"
              size="sm"
            >
              Copy Link
            </Button>
          </div>

          <div className="text-xs text-gray-500">
            <p>
              <strong>Current browser:</strong> {browserInfo.browserName} on {browserInfo.platform}
            </p>
            <p>
              <strong>In-app browser:</strong> {browserInfo.isInAppBrowser ? 'Yes' : 'No'}
            </p>
          </div>
        </div>

        <div className="flex justify-end">
          <Button 
            onClick={onClose}
            variant="ghost"
            size="sm"
            className="text-gray-500"
          >
            <X className="w-4 h-4 mr-1" />
            Dismiss
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InAppBrowserWarning;
